/**
 * brixton-search.js
 * 検索履歴 + サジェスト機能（Focal テーマ対応）
 *
 * サジェストの種類（上から順に表示）:
 *   1. 個人履歴        … Shopify Cart Attributes（サーバー保存、ユーザー固有）
 *   2. 人気キーワード  … Cloudflare Worker KV（全ユーザーの集計）
 *   3. 商品候補        … Shopify Predictive Search API（商品名・クエリ）
 *
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *  ⚠️  WORKER_URL を必ず設定してください
 *     workerUrl: 'https://brixton-search.YOUR_SUBDOMAIN.workers.dev'
 *     （デプロイ後に wrangler deploy で表示される URL）
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

(function () {
  'use strict';

  /* ====================================================
     設定
     ==================================================== */
  var CONFIG = {
    /* ▼ Cloudflare Worker の URL を設定 ▼ */
    workerUrl: 'https://brixton-search.oba-ad6.workers.dev',

    cartKey:          'bx_search_history',
    maxHistory:       8,
    maxHistoryShow:   3,   // 個人履歴は最大3件（多すぎない方が使いやすい）
    maxPopularShow:   10,  // 人気キーワードは最大10件
    maxPredictive:    4,   // Shopify 商品候補は最大4件
    debounceSearch:   280,
    debounceWrite:    600,
    minQueryLength:   1,
    predictiveEnabled: true
  };

  /* ====================================================
     Shopify Cart API ストレージ（個人履歴）
     ==================================================== */
  var CartStorage = {
    _cache:      null,
    _writeTimer: null,

    load: function () {
      if (this._cache !== null) return Promise.resolve(this._cache);
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function (cart) {
          var raw = cart.attributes && cart.attributes[CONFIG.cartKey];
          try   { CartStorage._cache = raw ? JSON.parse(raw) : []; }
          catch { CartStorage._cache = []; }
          return CartStorage._cache;
        })
        .catch(function () { CartStorage._cache = []; return []; });
    },

    filter: function (query) {
      return this.load().then(function (history) {
        if (!query) return history.slice(0, CONFIG.maxHistoryShow);
        var lower = query.toLowerCase();
        return history
          .filter(function (i) { return i.toLowerCase().indexOf(lower) !== -1; })
          .slice(0, CONFIG.maxHistoryShow);
      });
    },

    save: function (query) {
      query = (query || '').trim();
      if (!query) return Promise.resolve();
      return this.load().then(function (history) {
        history = history.filter(function (i) {
          return i.toLowerCase() !== query.toLowerCase();
        });
        history.unshift(query);
        CartStorage._cache = history.slice(0, CONFIG.maxHistory);
        CartStorage._scheduleWrite();
      });
    },

    remove: function (query) {
      return this.load().then(function (history) {
        CartStorage._cache = history.filter(function (i) { return i !== query; });
        return CartStorage._flush();
      });
    },

    clear: function () {
      CartStorage._cache = [];
      return CartStorage._flush();
    },

    _scheduleWrite: function () {
      clearTimeout(CartStorage._writeTimer);
      CartStorage._writeTimer = setTimeout(CartStorage._flush, CONFIG.debounceWrite);
    },

    _flush: function () {
      var payload = JSON.stringify(CartStorage._cache || []);
      var body    = { attributes: {} };
      body.attributes[CONFIG.cartKey] = payload;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).catch(function () {});
    }
  };

  /* ====================================================
     Cloudflare Worker API（全ユーザー集計）
     ==================================================== */
  var WorkerAPI = {

    /* 人気キーワードを取得 */
    suggest: function (query) {
      if (!CONFIG.workerUrl) return Promise.resolve([]);
      var url = CONFIG.workerUrl + '/suggest?q=' + encodeURIComponent(query || '');
      return fetch(url)
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function (data) { return data.suggestions || []; })
        .catch(function () { return []; });
    },

    /* 検索ワードをサーバーに記録（非同期・失敗しても問題なし） */
    track: function (query) {
      if (!CONFIG.workerUrl || !query) return;
      fetch(CONFIG.workerUrl + '/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: query }),
        keepalive: true   // ページ遷移後もリクエストを維持
      }).catch(function () {});
    }
  };

  /* ====================================================
     Shopify Predictive Search API（商品名候補）
     商品タイトルのみを返す
     ==================================================== */
  function fetchPredictive(query, callback) {
    if (!CONFIG.predictiveEnabled || (query || '').length < CONFIG.minQueryLength) {
      callback([]);
      return;
    }
    // /search?type=product&view=bx でサーバーサイド検索 → 商品タイトルの JSON 配列を返す
    // Predictive Search API のロケール問題を回避できる
    var url = '/search?type=product&q=' + encodeURIComponent(query) + '&view=bx';

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : Promise.reject(); })
      .then(function (text) {
        var results = JSON.parse(text.trim());
        if (!Array.isArray(results)) return callback([]);
        // [{title, url}, ...] 形式。タイトルと一致するものは除外
        results = results
          .filter(function (item) {
            var t = item && (item.title || item);
            return t && (t + '').toLowerCase() !== query.toLowerCase();
          })
          .slice(0, CONFIG.maxPredictive);
        callback(results);
      })
      .catch(function () { callback([]); });
  }

  /* ====================================================
     ユーティリティ
     ==================================================== */
  function escapeHtml(str) {
    return (str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function highlight(text, query) {
    var e = escapeHtml(text);
    if (!query) return e;
    var q = query.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    try { return e.replace(new RegExp('(' + q + ')', 'gi'), '<mark>$1</mark>'); }
    catch { return e; }
  }

  var ICONS = {
    history: '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    popular: '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
    search:  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    close:   '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
  };

  /* ====================================================
     各インスタンスの初期化
     ==================================================== */
  function initInstance(container) {
    var form         = container.querySelector('.bx-search-form');
    var input        = container.querySelector('.bx-search-input');
    var clearBtn     = container.querySelector('.bx-search-clear');
    var suggestions  = container.querySelector('.bx-suggestions');
    var historyEl    = container.querySelector('.bx-suggestions-history');
    var predictiveEl = container.querySelector('.bx-suggestions-predictive');
    var productsEl   = container.querySelector('.bx-suggestions-products');
    var emptyEl      = container.querySelector('.bx-suggestions-empty');

    if (!form || !input) return;

    var debounceTimer = null;
    var focusIndex    = -1;
    var allItems      = [];

    /* ── 描画: 個人履歴 ── */
    function renderHistory(query) {
      CartStorage.filter(query).then(function (items) {
        if (!items.length) { historyEl.innerHTML = ''; checkAndUpdate(); return; }

        var html = '<div class="bx-suggestions-section-label">'
                 +   '<span>検索履歴</span>'
                 +   '<button type="button" class="bx-suggestions-clear-all" data-action="clear-all">すべて削除</button>'
                 + '</div>';
        items.forEach(function (item) {
          html += '<button type="button" class="bx-suggestion-item" data-action="fill" data-value="' + escapeHtml(item) + '">'
                +   '<span class="bx-suggestion-item__icon">' + ICONS.history + '</span>'
                +   '<span class="bx-suggestion-item__text">' + highlight(item, query) + '</span>'
                +   '<span class="bx-suggestion-item__remove" data-action="remove" data-value="' + escapeHtml(item) + '" aria-label="' + escapeHtml(item) + ' を削除">' + ICONS.close + '</span>'
                + '</button>';
        });
        historyEl.innerHTML = html;
        checkAndUpdate();
      });
    }

    /* ── 描画: 人気キーワード（Worker） ── */
    function renderPopular(items, query, containerEl) {
      if (!items || !items.length) { containerEl.innerHTML = ''; checkAndUpdate(); return; }

      // 個人履歴と重複するワードは除外
      var cached    = CartStorage._cache || [];
      var histLower = cached.map(function (h) { return h.toLowerCase(); });
      var unique    = items.filter(function (i) {
        return histLower.indexOf(i.toLowerCase()) === -1;
      });

      if (!unique.length) { containerEl.innerHTML = ''; checkAndUpdate(); return; }

      var html = '<div class="bx-suggestions-divider"></div>'
               + '<div class="bx-suggestions-section-label"><span>人気キーワード</span></div>';
      unique.forEach(function (item) {
        html += '<button type="button" class="bx-suggestion-item" data-action="fill" data-value="' + escapeHtml(item) + '">'
              +   '<span class="bx-suggestion-item__icon bx-icon--popular">' + ICONS.popular + '</span>'
              +   '<span class="bx-suggestion-item__text">' + highlight(item, query) + '</span>'
              + '</button>';
      });
      containerEl.innerHTML = html;
      checkAndUpdate();
    }

    /* ── 描画: Shopify 商品候補 ── */
    function renderProductSuggestions(items, query) {
      if (!productsEl) return;
      if (!items || !items.length) { productsEl.innerHTML = ''; checkAndUpdate(); return; }

      // 履歴と重複するものを除外
      var cached    = CartStorage._cache || [];
      var histLower = cached.map(function (h) { return h.toLowerCase(); });
      var unique    = items.filter(function (item) {
        var t = item && (item.title || item);
        return histLower.indexOf((t + '').toLowerCase()) === -1;
      });

      if (!unique.length) { productsEl.innerHTML = ''; checkAndUpdate(); return; }

      var html = '<div class="bx-suggestions-divider"></div>'
               + '<div class="bx-suggestions-section-label"><span>商品候補</span></div>';
      unique.forEach(function (item) {
        var title = item.title || item;
        var url   = item.url || null;
        var img   = item.image || null;
        var thumb = img
          ? '<img class="bx-suggestion-item__img" src="' + escapeHtml(img) + '" alt="" width="40" height="40" loading="lazy">'
          : '<span class="bx-suggestion-item__icon">' + ICONS.search + '</span>';
        html += '<button type="button" class="bx-suggestion-item bx-suggestion-item--product"'
              +   ' data-action="' + (url ? 'go' : 'fill') + '"'
              +   ' data-value="' + escapeHtml(title) + '"'
              +   (url ? ' data-url="' + escapeHtml(url) + '"' : '')
              + '>'
              +   thumb
              +   '<span class="bx-suggestion-item__text">' + highlight(title, query) + '</span>'
              + '</button>';
      });
      productsEl.innerHTML = html;
      checkAndUpdate();
    }

    /* ── 空状態・フォーカスアイテム更新 ── */
    function checkAndUpdate() {
      var hasAny = historyEl.querySelector('.bx-suggestion-item')
                || predictiveEl.querySelector('.bx-suggestion-item')
                || (productsEl && productsEl.querySelector('.bx-suggestion-item'));
      hasAny ? emptyEl.setAttribute('hidden', '') : emptyEl.removeAttribute('hidden');
      updateItems();
    }

    function updateItems() {
      allItems = Array.prototype.slice.call(suggestions.querySelectorAll('.bx-suggestion-item'));
    }

    /* ── 表示/非表示 ── */
    function show() {
      suggestions.removeAttribute('hidden');
      input.setAttribute('aria-expanded', 'true');
      focusIndex = -1;
    }
    function hide() {
      suggestions.setAttribute('hidden', '');
      input.setAttribute('aria-expanded', 'false');
      focusIndex = -1;
      allItems.forEach(function (el) { el.classList.remove('is-focused'); });
    }

    /* ── キーボードナビ ── */
    function moveFocus(dir) {
      updateItems();
      if (!allItems.length) return;
      allItems.forEach(function (el) { el.classList.remove('is-focused'); });
      focusIndex += dir;
      if (focusIndex < -1)               focusIndex = allItems.length - 1;
      if (focusIndex >= allItems.length)  focusIndex = -1;
      if (focusIndex >= 0) {
        allItems[focusIndex].classList.add('is-focused');
        allItems[focusIndex].scrollIntoView({ block: 'nearest' });
      }
    }

    /* ── 入力変化 ── */
    function onInput() {
      var q = input.value.trim();
      clearBtn[q ? 'removeAttribute' : 'setAttribute']('hidden', '');

      // 個人履歴（即時）
      renderHistory(q);

      // 人気キーワード + 商品候補（デバウンス）
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        // Worker から人気キーワードを取得
        WorkerAPI.suggest(q).then(function (items) {
          renderPopular(items, q, predictiveEl);
        });
        // Shopify Predictive Search で商品候補を取得
        fetchPredictive(q, function (items) {
          renderProductSuggestions(items, q);
        });
      }, CONFIG.debounceSearch);

      show();
    }

    /* ── ワードをセットして送信 ── */
    function fillAndSubmit(value) {
      input.value = value;
      hide();
      // 履歴を保存 → 即フラッシュ（ページ遷移前に Cart API 書き込みを完了させる）
      CartStorage.save(value)
        .then(function () { return CartStorage._flush(); })
        .then(function () {
          WorkerAPI.track(value);
          form.submit();
        });
    }

    /* ====================================================
       イベントリスナー
       ==================================================== */

    // フォーカス時: ドロップダウンを開く
    input.addEventListener('focus', function () {
      var q = input.value.trim();
      renderHistory(q);
      WorkerAPI.suggest(q).then(function (items) {
        renderPopular(items, q, predictiveEl);
      });
      if (q) {
        fetchPredictive(q, function (items) {
          renderProductSuggestions(items, q);
        });
      }
      show();
    });

    // ── タッチデバイス（iOS/Android）用トグル ──────────────────────────
    // touchstart は mousedown より先に発火するため iOS のペーストメニューを防げる。
    // { passive: false } がないと Safari が preventDefault を無視する。
    // blur() することで次タップで focus イベントが発火し、ドロップダウンが再表示される。
    input.addEventListener('touchstart', function (e) {
      if (!suggestions.hasAttribute('hidden')) {
        e.preventDefault();  // iOS ペーストメニューを抑制 + 後続 mouse/click を防止
        hide();
        input.blur();        // キーボードを閉じ、次タップで focus → 再表示
      }
    }, { passive: false });

    // ── デスクトップ用トグル ──────────────────────────────────────────
    var closedByDown = false;
    input.addEventListener('mousedown', function (e) {
      if (document.activeElement === input && !suggestions.hasAttribute('hidden')) {
        e.preventDefault();
        hide();
        closedByDown = true;  // 直後の click で再表示しないためのフラグ
      }
    });
    input.addEventListener('click', function () {
      if (closedByDown) { closedByDown = false; return; }
      if (suggestions.hasAttribute('hidden')) {
        var q = input.value.trim();
        renderHistory(q);
        WorkerAPI.suggest(q).then(function (items) {
          renderPopular(items, q, predictiveEl);
        });
        if (q) {
          fetchPredictive(q, function (items) {
            renderProductSuggestions(items, q);
          });
        }
        show();
      }
    });

    input.addEventListener('input', onInput);

    input.addEventListener('keydown', function (e) {
      if      (e.key === 'ArrowDown')  { e.preventDefault(); moveFocus(1); }
      else if (e.key === 'ArrowUp')    { e.preventDefault(); moveFocus(-1); }
      else if (e.key === 'Enter') {
        if (focusIndex >= 0 && allItems[focusIndex]) {
          e.preventDefault();
          var v = allItems[focusIndex].dataset.value;
          if (v) fillAndSubmit(v);
        }
      }
      else if (e.key === 'Escape') { hide(); input.blur(); }
      else if (e.key === 'Tab')    { hide(); }
    });

    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.setAttribute('hidden', '');
      historyEl.innerHTML    = '';
      predictiveEl.innerHTML = '';
      if (productsEl) productsEl.innerHTML = '';
      emptyEl.removeAttribute('hidden');
      input.focus();
    });

    // サジェストクリック
    suggestions.addEventListener('click', function (e) {
      var el     = e.target.closest('[data-action]');
      if (!el) return;
      var action = el.dataset.action;
      var value  = el.dataset.value;

      if (action === 'go') {
        // 商品候補 → 直接商品ページへ遷移
        e.preventDefault();
        var dest = el.dataset.url;
        if (dest) { CartStorage.save(value); WorkerAPI.track(value); window.location.href = dest; }
      } else if (action === 'fill') {
        e.preventDefault();
        fillAndSubmit(value);
      } else if (action === 'remove') {
        e.preventDefault();
        e.stopPropagation();
        CartStorage.remove(value).then(function () {
          renderHistory(input.value.trim());
        });
        input.focus();
      } else if (action === 'clear-all') {
        e.preventDefault();
        CartStorage.clear().then(function () {
          historyEl.innerHTML = '';
          checkAndUpdate();
        });
        input.focus();
      }
    });

    // フォーム送信: 個人履歴保存 → 即フラッシュ → 遷移
    // （ページ離脱前に Cart API 書き込みを完了させるため e.preventDefault() を使う）
    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (!q) return;
      e.preventDefault();
      CartStorage.save(q)
        .then(function () { return CartStorage._flush(); })
        .then(function () {
          WorkerAPI.track(q);
          form.submit(); // submit() はイベントを発火しないので無限ループにならない
        });
    });

    // フォーカスアウト
    document.addEventListener('click',   function (e) { if (!container.contains(e.target)) hide(); });
    document.addEventListener('focusin', function (e) { if (!container.contains(e.target)) hide(); });

    container.dataset.bxInitialized = '1';
  }

  /* ====================================================
     トップページ「よく調べられている検索キーワード」の自動更新
     Worker の /popular データで .suggest-keywords リストを上書きする。
     データが空の場合はテーマ側の既存キーワードをそのまま維持する。
     ==================================================== */
  function updateSuggestKeywords() {
    var list = document.querySelector('.suggest-keywords');
    if (!list || !CONFIG.workerUrl) return;
    if (list.dataset.bxUpdated) return; // 二重実行防止
    list.dataset.bxUpdated = '1';

    // 管理画面で設定済みのキーワードを取得（重複チェック用）
    var existingWords = Array.prototype.slice.call(list.querySelectorAll('li')).map(function (li) {
      return (li.textContent || '').trim().toLowerCase();
    });

    fetch(CONFIG.workerUrl + '/popular?limit=20')
      .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
      .then(function (data) {
        var popular = data.popular || [];
        if (!popular.length) return; // データなし → 既存キーワードを維持

        // 既存と重複しないものを最大3件抽出
        var newItems = popular
          .filter(function (item) {
            return existingWords.indexOf(item.q.toLowerCase()) === -1;
          })
          .slice(0, 4);

        if (!newItems.length) return;

        // 管理画面キーワードの末尾に追加（設定 → 自動の順）
        var html = newItems.map(function (item) {
          return '<li><a href="/search?q=' + encodeURIComponent(item.q) + '">'
               + escapeHtml(item.q) + '</a></li>';
        }).join('');
        list.insertAdjacentHTML('beforeend', html);
      })
      .catch(function () {}); // エラー時も既存キーワードを維持
  }

  /* ====================================================
     起動: ページロード時にカート履歴をプリフェッチ
     ==================================================== */
  function initAll() {
    document.querySelectorAll('.bx-search-container:not([data-bx-initialized])').forEach(initInstance);
    CartStorage.load(); // バックグラウンドでカート履歴を先読み
    updateSuggestKeywords(); // 人気キーワードセクションを更新
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();
